import React, { Component } from 'react';
import {
  SearchFilled,
  DocumentFilled,
  FolderFilled,
  SettingsFilled,
  ImageFilled,
  MailFilled,
  ChatFilled,
  PeopleFilled,
} from '@fluentui/react-icons';
import msAppSource from './assets/MS_AppSource.webp';
import './pentities.css';
import './SearchAssistant.css';
import NavBar from './NavBar';

interface SearchAssistantPageProps {}

interface SearchAssistantPageState {
  isVideoOpen: boolean;
}

class SearchAssistantPage extends Component<SearchAssistantPageProps, SearchAssistantPageState> {
  constructor(props: SearchAssistantPageProps) {
    super(props);
    this.state = { isVideoOpen: false };
  }

  openVideo = () => this.setState({ isVideoOpen: true });
  closeVideo = () => this.setState({ isVideoOpen: false });

  render() {
    return (
      <div className="pentities-page searchassistant-v2-page">
        {/* Top Navigation */}
        <NavBar />

        {/* Hero Section */}
        <section className="pentities-hero-section">
          <div className="pentities-hero-video-background">
            <video preload="metadata" autoPlay loop muted playsInline className="pentities-hero-video">
              <source src={require('./assets/video/web/19759482-hd_1920_1080_24fps.mp4')} type="video/mp4" />
            </video>
          </div>
          <div className="pentities-container">
            <div className="pentities-hero-content">
              <span className="pentities-hero-eyebrow">Intelligent Search</span>
              <h1 className="pentities-hero-title">
                Find anything across Microsoft 365 and act on it without leaving search
              </h1>
              <p className="pentities-hero-description">
                Search Assistant brings custom refinement filters to large libraries and unifies search across
                SharePoint, OneDrive, emails, Teams chats and contacts with in-place preview, metadata editing,
                sharing and version control, all from one interface.
              </p>
              <div className="pentities-hero-cta">
                <a
                  href="https://outlook.office.com/book/META365Bookingforademo@e-study.gr/?ismsaljsauthenabled"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pentities-hero-cta-btn"
                >
                  Request Demo
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* General Description */}
        <section id="overview" className="pentities-gendesc-section">
          <div className="pentities-container">
            <div className="pentities-gendesc-content">
              <span className="pentities-gendesc-label">What is Search Assistant?</span>
              <h2 className="pentities-gendesc-title">Intelligent search that adapts to your content and your workflow</h2>
              <p>
                Searching vast amounts of content is hard you often need to filter by specific fields or hunt
                across scattered metadata. Search Assistant lets you define custom refinement filters, which makes
                it especially powerful in large-volume libraries.
              </p>
              <p>
                <b>Preview everything.</b> Continuous document preview means you can always open and review the
                content you care about, without leaving the results.
              </p>
              <p>
                <b>Work across sources.</b> Select multiple lists or libraries at once, and search everything from
                SharePoint documents to emails, chat messages and contacts all from one unified interface with
                intelligent filtering and metadata management.
              </p>
            </div>
          </div>
        </section>

        {/* Key Features dark */}
        <section id="features" className="pentities-docmgmt-section">
          <div className="pentities-container">
            <span className="pentities-docmgmt-eyebrow">Key features</span>
            <h2 className="pentities-docmgmt-title">Comprehensive search and content management, in one place</h2>
            <p className="pentities-docmgmt-lead">
              Filter precisely, preview in place, and manage metadata, permissions and versions right from your
              results.
            </p>

            <div className="pentities-docmgmt-block">
              <h3>1 · Custom refinement filters</h3>
              <p>
                Define custom refinement filters on metadata fields lookups, choice fields and managed metadata.
                Each field automatically loads all of its possible values for quick, precise filtering.
              </p>
            </div>
            <div className="pentities-docmgmt-block">
              <h3>2 · Multi-source search</h3>
              <p>
                Search across SharePoint sites, document libraries, lists, emails, Teams chat messages and personal
                contacts all from a single unified interface.
              </p>
            </div>
            <div className="pentities-docmgmt-block">
              <h3>3 · Continuous document preview</h3>
              <p>
                Access and review content with built-in preview. View documents without leaving search and work
                with them directly in the preview panel.
              </p>
            </div>
            <div className="pentities-docmgmt-block">
              <h3>4 · Metadata management</h3>
              <p>
                Edit metadata straight from the results person fields, managed metadata, sensitivity labels and
                every other property without navigating to the source library.
              </p>
            </div>
            <div className="pentities-docmgmt-block">
              <h3>5 · Permissions &amp; sharing</h3>
              <p>
                Change permissions and share documents or list items with others directly from the search
                interface, streamlining collaboration without switching contexts.
              </p>
            </div>
            <div className="pentities-docmgmt-block">
              <h3>6 · Version history</h3>
              <p>
                View and restore previous versions of documents and list items, see exactly what changed in each,
                and work with historical content seamlessly.
              </p>
            </div>
          </div>
        </section>

        {/* Content Types icon card grid */}
        <section id="content-types" className="sa-ct-section">
          <div className="pentities-container">
            <span className="sa-ct-eyebrow">Search every content type</span>
            <h2 className="sa-ct-title">Intelligent search across every kind of content</h2>
            <p className="sa-ct-lead">
              One search, with the right actions for each result type across eight dedicated views.
            </p>

            <div className="sa-ct-grid">
              <div className="sa-ct-card">
                <div className="sa-ct-icon"><SearchFilled /></div>
                <h3>All content</h3>
                <p>Files, list items, folders, sites and image names in one unified view across your whole Microsoft 365 environment.</p>
              </div>
              <div className="sa-ct-card">
                <div className="sa-ct-icon"><DocumentFilled /></div>
                <h3>Files</h3>
                <p>Filter to just files and enable a live preview to read and work with documents without leaving the interface.</p>
              </div>
              <div className="sa-ct-card">
                <div className="sa-ct-icon"><FolderFilled /></div>
                <h3>Folders</h3>
                <p>Every folder matching your search, with one-click navigation to the structures across your organisation.</p>
              </div>
              <div className="sa-ct-card">
                <div className="sa-ct-icon"><SettingsFilled /></div>
                <h3>Sites</h3>
                <p>Discover SharePoint sites matching your terms and jump straight to them no browsing the whole collection.</p>
              </div>
              <div className="sa-ct-card">
                <div className="sa-ct-icon"><ImageFilled /></div>
                <h3>Images</h3>
                <p>Preview images inline and navigate to their source locations ideal for managing visual content.</p>
              </div>
              <div className="sa-ct-card">
                <div className="sa-ct-icon"><MailFilled /></div>
                <h3>Emails</h3>
                <p>Search your email messages and navigate directly to them find communications without switching to Outlook.</p>
              </div>
              <div className="sa-ct-card">
                <div className="sa-ct-icon"><ChatFilled /></div>
                <h3>Chat messages</h3>
                <p>Search Teams chat messages and open the conversation surface key discussions and decisions fast.</p>
              </div>
              <div className="sa-ct-card">
                <div className="sa-ct-icon"><PeopleFilled /></div>
                <h3>People</h3>
                <p>Find persons or groups across your organisation by name, department or other attributes.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Easy Installation white */}
        <section id="installation" className="pentities-workflow-section">
          <div className="pentities-container">
            <span className="pentities-workflow-eyebrow">Easy installation</span>
            <h2 className="pentities-workflow-title">Up and running in three simple steps</h2>
            <p className="pentities-workflow-lead">
              Configured once by someone with app-install permissions, then ready for everyone and reconfigurable
              anytime from the settings panel, with a one-click "return to default".
            </p>

            <div className="pentities-workflow-block">
              <h3>1 · Select the list</h3>
              <p>
                Choose the list or library from the Team where the app is installed. You can change the main list
                or library later, even from a different site. The initial configuration is set by personnel with
                app-installation permissions.
              </p>
            </div>
            <div className="pentities-workflow-block">
              <h3>2 · Choose search fields</h3>
              <p>
                Select the fields to search and refine on. Each automatically loads all its possible values lookup, choice or managed metadata enabling powerful filtering tailored to your content.
              </p>
            </div>
            <div className="pentities-workflow-block">
              <h3>3 · Define editable fields</h3>
              <p>
                Select which fields can be edited from the results (or choose "Select All") for complete control
                over what metadata can be modified directly from the search interface.
              </p>
            </div>
          </div>
        </section>

        {/* Platform & ecosystem combined card/chip section */}
        <section id="platform" className="sa-platform-section">
          <div className="pentities-container">
            <span className="sa-platform-eyebrow">The Search Assistant platform</span>
            <h2 className="sa-platform-title">Everything it supports and the suite it belongs to</h2>

            <div className="sa-feature-grid">
              <div className="sa-feature-card">
                <h3>Supported environments</h3>
                <div className="sa-chips">
                  <span>Microsoft Teams</span>
                  <span>Outlook</span>
                  <span>SharePoint</span>
                  <span>Microsoft 365</span>
                  <span>Office</span>
                </div>
              </div>

              <div className="sa-feature-card">
                <h3>Search across</h3>
                <div className="sa-chips">
                  <span>SharePoint</span>
                  <span>OneDrive</span>
                  <span>Teams</span>
                  <span>Mails</span>
                  <span>Contracts</span>
                  <span>Persons</span>
                  <span>Chat messages</span>
                </div>
              </div>

              <div className="sa-feature-card sa-feature-card-wide">
                <h3>Core features</h3>
                <ul className="sa-check-list">
                  <li>Change permissions and data access</li>
                  <li>Insert, update and change items &amp; metadata</li>
                  <li>People pickers and Term Sets supported</li>
                  <li>Work directly with files and list items</li>
                  <li>Navigate to original location, mail or chat message</li>
                  <li>See and use previous versions</li>
                  <li>Retain existing permissions &amp; sensitivity labels</li>
                  <li>Navigate inside document-library folders</li>
                  <li>Download files and create shareable links</li>
                  <li>Pin different lists</li>
                  <li>Persist configuration across environments</li>
                </ul>
              </div>
            </div>

          </div>
        </section>

        {/* Demo Video Section dark */}
        <section className="demo-video-section">
          <div className="pentities-container">
            <div className="pentities-demo-layout">
              <div className="pentities-demo-content">
                <span className="pentities-demo-eyebrow">See Search Assistant in Action</span>
                <h2 className="pentities-demo-title">
                  Watch how Search Assistant transforms content discovery
                </h2>
                <p>
                  In a few minutes you'll see custom refinement filters cut through a large library, a document
                  previewed in place, metadata edited from the results, and a single search reaching across
                  SharePoint, email and Teams chat.
                </p>
                <p>
                  Everything shown runs inside your own Microsoft 365 tenant, using the permissions your people
                  already have.
                </p>
              </div>
              <div className="pentities-demo-media">
                <div
                  className="pentities-demo-video-wrapper"
                  onClick={this.openVideo}
                  role="button"
                  tabIndex={0}
                  aria-label="Play Search Assistant product presentation"
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') this.openVideo(); }}
                >
                  <img loading="lazy" decoding="async"
                    className="pentities-demo-video-thumb"
                    src="https://img.youtube.com/vi/qbrE5iJQS3g/maxresdefault.jpg"
                    alt="Search Assistant Product Presentation"
                  />
                  <div className="pentities-demo-play-overlay">
                    <svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" className="pentities-demo-play-icon">
                      <circle cx="32" cy="32" r="32" fill="rgba(0,0,0,0.55)" />
                      <polygon points="24,16 52,32 24,48" fill="#ffffff" />
                    </svg>
                  </div>
                </div>
                <p className="pentities-demo-video-caption">Click to watch the full presentation</p>
              </div>
            </div>
            <div className="download-section">
              <h3 className="download-title">Get Search Assistant Now</h3>
              <p className="download-description">
                Download Search Assistant directly from Microsoft AppSource and revolutionize your content discovery.
              </p>
              <a
                href="https://marketplace.microsoft.com/en-us/product/WA200008609?tab=Overview"
                target="_blank"
                rel="noopener noreferrer"
                className="appsource-link"
              >
                <img loading="lazy" decoding="async" src={msAppSource} alt="Get it from Microsoft AppSource" className="appsource-badge" />
              </a>
            </div>
          </div>
        </section>

        {/* Part of the Meta-365 suite standalone */}
        <section className="pentities-modules-section">
          <div className="pentities-container">
            <span className="pentities-modules-eyebrow">Meta‑365 Suite</span>
            <h2 className="pentities-modules-title">Part of the Meta‑365 suite</h2>
            <p className="pentities-modules-lead">
              Search Assistant works alongside the rest of the Meta‑365 platform each product native to
              Microsoft 365 and built on the data you already own.
            </p>

            <div className="pentities-modules-grid">
              <a href="/solutions/pentities" className="pentities-module-card">
                <h3>pEntities Platform</h3>
                <p>
                  Contracts, cases, HR records and projects become living workspaces inside Microsoft Teams.
                  Every record carries its documents, emails, tasks and approvals in one place, with a domain AI
                  agent that reads the data you already own and surfaces the risks and next steps that matter.
                </p>
                <span className="pentities-module-link">Explore →</span>
              </a>
              <a href="/solutions/docsearch" className="pentities-module-card">
                <h3>DocSearch</h3>
                <p>
                  Unified search and content management across OneDrive, SharePoint and Teams. Find any file,
                  email or list item by content, metadata or type from a single box no matter where it lives and organise, preview and act on results without ever leaving Microsoft 365.
                </p>
                <span className="pentities-module-link">Explore →</span>
              </a>
              <a href="/solutions/pshareemail" className="pentities-module-card">
                <h3>PShareEmail</h3>
                <p>
                  Save Outlook emails straight to SharePoint, OneDrive or Teams and share them to the right
                  channels in a couple of clicks. Attachments, metadata and conversation context are preserved,
                  so important correspondence lands where your team can actually find and reuse it.
                </p>
                <span className="pentities-module-link">Explore →</span>
              </a>
              <a href="/solutions/automation" className="pentities-module-card">
                <h3>Workflow Automation</h3>
                <p>
                  No-code workflows that trigger on your own data and run themselves. Build multi-step approvals,
                  notifications and scheduled routines that react to changes across the suite automating the
                  repetitive work your teams do every day without writing a single line of code.
                </p>
                <span className="pentities-module-link">Explore →</span>
              </a>
            </div>
          </div>
        </section>

        {/* Video Modal */}
        {this.state.isVideoOpen && (
          <div className="pentities-video-modal" onClick={this.closeVideo} role="dialog" aria-modal="true" aria-label="Search Assistant product presentation">
            <button className="pentities-video-close" onClick={this.closeVideo} aria-label="Close video">✕</button>
            <div className="pentities-video-frame" onClick={(e) => e.stopPropagation()}>
              <iframe
                src="https://www.youtube.com/embed/qbrE5iJQS3g?autoplay=1&rel=0"
                title="Search Assistant Product Presentation"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="footer">
          <div className="container">
            <div className="footer-grid">
              <div className="footer-column">
                <h4>Products</h4>
                <ul>
                  <li><a href="/solutions/pentities">pEntities</a>
                    <ul className="footer-subnav">
                      <li><a href="/solutions/automation">Automation Module</a></li>
                      <li><a href="/solutions/ticketing">Ticketing Module</a></li>
                      <li><a href="/solutions/hr-template">HR Module</a></li>
                      <li><a href="/solutions/case-management">Legal Module</a></li>
                      <li><a href="/solutions/hr-hub">Employee Module</a></li>
                    </ul>
                  </li>
                  <li><a href="/solutions/pshareemail">PShareEmail</a></li>
                  <li><a href="/solutions/docsearch">DocSearch Tool</a></li>
                  <li><a href="/solutions/searchassistant">Search Assistant</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <ul>
                  <li><a href="/about">About Us</a></li>
                  <li><a href="/get-started">Get Started</a></li>
                  <li><a href="/contactus">Contact Us</a></li>
                  <li><a href="/plans-pricing">Plans & Pricing</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Support</h4>
                <ul>
                  <li><a href="/knowledge">Knowledge Base</a></li>
                  <li><a href="/technical-support">Technical Support</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Contact Us</h4>
                <div className="footer-office">
                  <p className="office-name">Athens Office</p>
                  <p>365 Kifisias Avenue, Kifisia, 14561</p>
                  <p>Ground Floor, Inside the Arcade</p>
                  <p>Tel: (+30)-210-260-3764</p>
                </div>
                <div className="footer-office">
                  <p className="office-name">Thessaloniki Office</p>
                  <p>15 Nikiforou Ouranou & Minotavrou Streets</p>
                  <p>Thessaloniki, 54627, PORTO CENTER, Building G1, 3rd Floor</p>
                  <p>Tel: (+30)-231-005-3361</p>
                </div>
                <div className="footer-office">
                  <p className="office-name">Crete Office</p>
                  <p>Kisamos, Chania</p>
                  <p>Tel: (+30)-2822-051309</p>
                </div>
                <p className="footer-email">info@meta-365.com</p>
              </div>
            </div>
            <div className="footer-bottom">
              <p>&copy; 2026 Meta-365. All rights reserved.</p>
              <div className="footer-social">
                <a href="https://www.linkedin.com/company/meta-365" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><img loading="lazy" decoding="async" src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='white'%3E%3Cpath%20d='M4.98%203.5C4.98%204.88%203.87%206%202.5%206S0%204.88%200%203.5%201.12%201%202.5%201s2.48%201.12%202.48%202.5zM.25%208h4.5v13H.25V8zM8.5%208h4.31v1.78h.06c.6-1.14%202.07-2.34%204.26-2.34C21.5%207.44%2022.5%2010%2022.5%2014.02V21H18v-6.1c0-1.46-.03-3.33-2.03-3.33-2.03%200-2.34%201.58-2.34%203.22V21H8.5V8z'/%3E%3C/svg%3E" alt="LinkedIn" width="18" height="18" /></a>
                <a href="https://www.facebook.com/profile.php?id=61589941284929" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><img loading="lazy" decoding="async" src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='white'%3E%3Cpath%20d='M24%2012.07C24%205.4%2018.63%200%2012%200S0%205.4%200%2012.07C0%2018.1%204.39%2023.1%2010.13%2024v-8.44H7.08v-3.49h3.05V9.41c0-3.02%201.79-4.69%204.53-4.69%201.31%200%202.68.24%202.68.24v2.97h-1.51c-1.49%200-1.96.93-1.96%201.89v2.25h3.33l-.53%203.49h-2.8V24C19.61%2023.1%2024%2018.1%2024%2012.07z'/%3E%3C/svg%3E" alt="Facebook" width="18" height="18" /></a>
              </div>
              <div className="footer-links">
                <a href="/privacy-policy">Privacy Policy</a>
                <a href="/terms-of-use">Terms & Conditions</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    );
  }
}

export default SearchAssistantPage;
